import React, { useEffect, useMemo } from 'react'
import { SelectInput } from 'components/forms'
import { useField, useFormikContext } from 'formik'
import { TimeRange } from './time-range-enum'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { timeRangeValue } from './time-range-value'

const moment_formats: {[k: string]: string} = {
    'date': 'YYYY-MM-DD',
    'gdate': 'DD.MM.YYYY',
}

type TimeRangeProps = {
    name: string,
    label?: string,
    format?: string,
    nameDateFrom: string,
    nameDateTo: string
}

export const IxTimeRange = ({
    name,
    label,
    format='date',
    nameDateFrom,
    nameDateTo,
}: TimeRangeProps) => {
    const { setFieldValue } = useFormikContext()
    const [field] = useField(name)

    const { t } = useTranslation()

    useEffect(() => {
        const halfYear = Math.trunc((moment().month()+1)/6)
        const date = moment().date()
        const timeRanges = {
            [TimeRange.eTimeRangeToday]: {from: moment().format(moment_formats[format]), to: moment().format(moment_formats[format])},
            [TimeRange.eTimeRangeThisWeek]: timeRangeValue(moment_formats[format], {range: 1, type: 'day'}, {range: 1, type: 'day'}, 'week', 'week'),
            [TimeRange.eTimeRangeThisMonth]: timeRangeValue(moment_formats[format], null, null, 'month', 'month'),
            [TimeRange.eTimeRangeNextMonth]: timeRangeValue(moment_formats[format], {range: 1, type: 'month'}, {range: 1, type: 'month'}, 'month', 'month'),
            [TimeRange.eTimeRangeLastMonth]: timeRangeValue(moment_formats[format], {range: -1, type: 'month'}, {range: -1, type: 'month'}, 'month', 'month'),
            [TimeRange.eTimeRange2MonthAgo]: timeRangeValue(moment_formats[format], {range: -2, type: 'month'},{range: -2, type: 'month'}, 'month', 'month'),
            [TimeRange.eTimeRangeThisMonthTodayMinus15]: date <= 15 ? timeRangeValue(moment_formats[format], {range: -1, type: 'month'}, {range: -1, type: 'month'}, 'month', 'month') : timeRangeValue(moment_formats[format], null, null, 'month', 'month'),
            [TimeRange.eTimeRangeLast7Days]: timeRangeValue(moment_formats[format], {range: -7, type: 'day'}, 'day', 'day'),
            [TimeRange.eTimeRangeLast14Days]: timeRangeValue(moment_formats[format], {range: -14, type: 'day'}, 'day', 'day'),
            [TimeRange.eTimeRangeLast3Months]: timeRangeValue(moment_formats[format], {range: -3, type: 'month'}, 'day', 'day'),
            [TimeRange.eTimeRangeLast6Months]: timeRangeValue(moment_formats[format], {range: -6, type: 'month'}, 'day', 'day'),
            // [TimeRange.eTimeRangeActWQuartar]: {from: moment().add(-2, 'months').startOf('month').format('YYYY-MM-DD'), to: moment().endOf('month').format('YYYY-MM-DD')},
            // [TimeRange.eTimeRangeActWHalfYear]: {from: moment().add(-5, 'months').startOf('month').format('YYYY-MM-DD'), to: moment().endOf('month').format('YYYY-MM-DD')},
            // [TimeRange.eTimeRangeActWJahrMonthUltimo]: {from: moment().add(-5, 'months').startOf('month').format('YYYY-MM-DD'), to: moment().endOf('month').format('YYYY-MM-DD')},
            // [TimeRange.eTimeRangeActWJahr]: {from: moment().startOf('year').format('YYYY-MM-DD'), to: moment().endOf('year').format('YYYY-MM-DD')},
            // [TimeRange.eTimeRangeWLastQuartar]: {from: moment().add(-3, 'months').startOf('month').format('YYYY-MM-DD'), to: moment().add(-1, 'months').endOf('month').format('YYYY-MM-DD')},
            // [TimeRange.eTimeRangeWLastHalfYear]: {from: moment().format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD')},
            // [TimeRange.eTimeRangeLastWJahr]: {from: moment().add(-1, 'years').startOf('year').format('YYYY-MM-DD'), to: moment().add(-1, 'years').endOf('year').format('YYYY-MM-DD')},
            [TimeRange.eTimeRangeThisQuartar]: timeRangeValue(moment_formats[format], null, {range: 2, type: 'month'}, 'month', 'month'),
            [TimeRange.eTimeRangeThisHalfYear]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: halfYear ? 'July' : 'January', end: halfYear ? 'December' : 'June'}),
            [TimeRange.eTimeRangeThisYearMonthUltimo]: timeRangeValue(moment_formats[format], null, {range: -1, type: 'month'}, 'year', 'month'),
            [TimeRange.eTimeRangeThisYear]: timeRangeValue(moment_formats[format], null, null, 'year', 'year'),
            [TimeRange.eTimeRangeLastQuartar]: timeRangeValue(moment_formats[format], {range: -3, type: 'month'}, {range: -1, type: 'month'}, 'month', 'month'),
            [TimeRange.eTimeRangeLastHalfYear]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: halfYear ? 'January' : 'July', end: halfYear ? 'June' : 'December'}),
            [TimeRange.eTimeRangeLastYear]: timeRangeValue(moment_formats[format], {range: -1, type: 'year'}, {range: -1, type: 'year'}, 'year', 'year'),
            [TimeRange.eTimeRangeJanuary]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'January', end: 'January'}),
            [TimeRange.eTimeRangeFebruary]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'February', end: 'February'}),
            [TimeRange.eTimeRangeMarch]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'March', end: 'March'}),
            [TimeRange.eTimeRangeApril]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'April', end: 'April'}),
            [TimeRange.eTimeRangeMay]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'May', end: 'May'}),
            [TimeRange.eTimeRangeJune]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'June', end: 'June'}),
            [TimeRange.eTimeRangeJuly]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'July', end: 'July'}),
            [TimeRange.eTimeRangeAugust]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'August', end: 'August'}),
            [TimeRange.eTimeRangeSeptember]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'September', end: 'September'}),
            [TimeRange.eTimeRangeOctober]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'October', end: 'October'}),
            [TimeRange.eTimeRangeNovember]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'November', end: 'November'}),
            [TimeRange.eTimeRangeDecember]: timeRangeValue(moment_formats[format], null, null, 'month', 'month', {start: 'December', end: 'December'}),
        } as any

        if (field.value >= 0) {
            if (field.value === TimeRange.eTimeRangeUserdefined) { return }
            else {
                setFieldValue(nameDateFrom, timeRanges[field.value]?.from, true)
                setFieldValue(nameDateTo, timeRanges[field.value]?.to, true)
            }
        }

    },[setFieldValue, field.value, format, nameDateFrom, nameDateTo])

    const timeRangeOptions = useMemo(() => ([
        {name: t('global:timeRange.manual'), value: TimeRange.eTimeRangeUserdefined},
		{name: t('global:timeRange.today'), value: TimeRange.eTimeRangeToday},
		{name: t('global:timeRange.thisWeek'), value: TimeRange.eTimeRangeThisWeek},
		{name: t('global:timeRange.thisMonth'), value: TimeRange.eTimeRangeThisMonth},
		{name: t('global:timeRange.nextMonth'), value: TimeRange.eTimeRangeNextMonth},
		{name: t('global:timeRange.previousMonth'), value: TimeRange.eTimeRangeLastMonth},
		{name: t('global:timeRange.twoMonthsAgo'), value: TimeRange.eTimeRange2MonthAgo},
		{name: t('global:timeRange.thisMonthTodayMinus15'), value: TimeRange.eTimeRangeThisMonthTodayMinus15},
		{name: t('global:timeRange.lastSevenDays'), value: TimeRange.eTimeRangeLast7Days},
		{name: t('global:timeRange.lastFourteenDays'), value: TimeRange.eTimeRangeLast14Days},
		{name: t('global:timeRange.lastThreeMonths'), value: TimeRange.eTimeRangeLast3Months},
		{name: t('global:timeRange.lastSixMonths'), value: TimeRange.eTimeRangeLast6Months},
		// {name: 'FY - current quarter', value: TimeRange.eTimeRangeActWQuartar},
		// {name: 'FY - current half of year', value: TimeRange.eTimeRangeActWHalfYear},
		// {name: 'FY - till end of previous month', value: TimeRange.eTimeRangeActWJahrMonthUltimo},
		// {name: 'FY - current year', value: TimeRange.eTimeRangeActWJahr},
		// {name: 'FY - last quarter', value: TimeRange.eTimeRangeWLastQuartar},
		// {name: 'FY - last half year', value: TimeRange.eTimeRangeWLastHalfYear},
		// {name: 'FY - last year', value: TimeRange.eTimeRangeLastWJahr},
		{name: t('global:timeRange.cYcurrentQuarter'), value: TimeRange.eTimeRangeThisQuartar},
		{name: t('global:timeRange.cYcurrentHalfYear'), value: TimeRange.eTimeRangeThisHalfYear},
		{name: t('global:timeRange.cYtillEndOfPreviousMonth'), value: TimeRange.eTimeRangeThisYearMonthUltimo},
		{name: t('global:timeRange.cYthisYear'), value: TimeRange.eTimeRangeThisYear},
		{name: t('global:timeRange.cYlastQuarter'), value: TimeRange.eTimeRangeLastQuartar},
		{name: t('global:timeRange.cYlastHalfYear'), value: TimeRange.eTimeRangeLastHalfYear},
		{name: t('global:timeRange.cYlastYear'), value: TimeRange.eTimeRangeLastYear},
		{name: t('global:timeRange.january'), value: TimeRange.eTimeRangeJanuary},
		{name: t('global:timeRange.february'), value: TimeRange.eTimeRangeFebruary},
		{name: t('global:timeRange.march'), value: TimeRange.eTimeRangeMarch},
		{name: t('global:timeRange.april'), value: TimeRange.eTimeRangeApril},
		{name: t('global:timeRange.may'), value: TimeRange.eTimeRangeMay},
		{name: t('global:timeRange.june'), value: TimeRange.eTimeRangeJune},
		{name: t('global:timeRange.july'), value: TimeRange.eTimeRangeJuly},
		{name: t('global:timeRange.august'), value: TimeRange.eTimeRangeAugust},
		{name: t('global:timeRange.september'), value: TimeRange.eTimeRangeSeptember},
		{name: t('global:timeRange.october'), value: TimeRange.eTimeRangeOctober},
        {name: t('global:timeRange.november'), value: TimeRange.eTimeRangeNovember},
		{name: t('global:timeRange.december'), value: TimeRange.eTimeRangeDecember}
    ]),[t])

    return (
        <SelectInput opts={timeRangeOptions} name={name} label={label || t('global:timeRange.timeRange')}/>
    )
}
